import { Checkbox, Form, Input, InputNumber, Select, TimePicker } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useBrigadeList } from "@/modules/brigades/hooks/useBrigade";
import { useOrganizationTypePositions } from "@/modules/organization-type-positions/hooks/useOrganizationTypePositions";
import { ImageUpload } from "@/shared/components/ImageUpload";
import { PhoneInput } from "@/shared/components/PhoneInput";
import { useStaffStore } from "../store/staff.store";
import type { StaffFormValues, StaffType } from "../types";

/** Dushanba → Yakshanba. Backend: 1=Mon, 2=Tue, ..., 6=Sat, 0=Sun */
const DAY_OPTIONS = [
	{ value: 1, label: "Dushanba" },
	{ value: 2, label: "Seshanba" },
	{ value: 3, label: "Chorshanba" },
	{ value: 4, label: "Payshanba" },
	{ value: 5, label: "Juma" },
	{ value: 6, label: "Shanba" },
	{ value: 0, label: "Yakshanba" },
];

const STAFF_TYPE_OPTIONS: { value: StaffType; label: string }[] = [
	{ value: "EMPLOYEE", label: "Xodim" },
	{ value: "BRIGADIER", label: "Brigadir" },
	{ value: "SECURITY", label: "Xavfsizlik" },
	{ value: "ADMIN", label: "Admin" },
	{ value: "MANAGER", label: "Menejer" },
];

/** Org staff: EMPLOYEE excluded (only brigade members can be EMPLOYEE) */
const ORG_STAFF_TYPE_OPTIONS = STAFF_TYPE_OPTIONS.filter((o) => o.value !== "EMPLOYEE");

interface StaffFormProps {
	form: ReturnType<typeof Form.useForm<StaffFormValues>>[0];
	organizationId: string;
	hideOrganization?: boolean;
	organizations: { id: string; name: string }[];
	brigadeId?: string;
}

export function StaffForm({
	form,
	organizationId,
	hideOrganization = false,
	organizations,
	brigadeId,
}: StaffFormProps) {
	const editingItem = useStaffStore((s) => s.editingItem);
	const showOrganization = !(editingItem || hideOrganization);
	const { data: positionsData, isLoading: positionsLoading } = useOrganizationTypePositions({
		page: 1,
		limit: 100,
	});

	// Brigade select: only EMPLOYEE staff can belong to a brigade (backend rule)
	const watchedOrganizationId = Form.useWatch("organizationId", form);
	const watchedStaffType = Form.useWatch("staffType", form);
	const effectiveOrganizationId = watchedOrganizationId ?? organizationId;
	const showBrigadeSelect = Boolean(brigadeId) || watchedStaffType === "EMPLOYEE";
	const { data: brigadesData, isLoading: brigadesLoading } = useBrigadeList({
		organizationId: effectiveOrganizationId,
		page: 1,
		limit: 100,
	});

	useEffect(() => {
		if (editingItem) {
			const firstWorkday = editingItem.workdays?.[0];
			form.setFieldsValue({
				fullname: editingItem.fullname,
				phoneNumber: editingItem.phoneNumber ?? undefined,
				jobEntryDate: editingItem.jobEntryDate ?? undefined,
				photoId: editingItem.photoId ?? undefined,
				positionId: editingItem.positionId ?? undefined,
				staffType:
					(editingItem as typeof editingItem & { staffType?: StaffType }).staffType ?? "EMPLOYEE",
				brigadeId: editingItem.brigadeId ?? brigadeId ?? undefined,
				workdays:
					editingItem.workdays
						?.filter((w) => w.dayNumber != null)
						.map((w) => w.dayNumber)
						.filter((d, i, arr) => arr.indexOf(d) === i) ?? [],
				workStart: firstWorkday?.start ?? "08:00:00",
				workEnd: firstWorkday?.end ?? "18:00:00",
				maxHoursPerWeek: editingItem.maxHoursPerWeek ?? 24,
				maxHoursPerDay: editingItem.maxHoursPerDay ?? 7,
			});
		} else {
			form.resetFields();
		}
	}, [editingItem, form, brigadeId]);

	return (
		<Form
			form={form}
			layout="vertical"
			requiredMark={false}
			className="mt-4"
			initialValues={{
				organizationId: organizationId,
				staffType: brigadeId ? "EMPLOYEE" : "ADMIN",
				brigadeId: brigadeId,
				maxHoursPerWeek: 24,
				maxHoursPerDay: 7,
			}}
		>
			<div className="grid grid-cols-2 gap-4">
				<Form.Item
					name="fullname"
					label="To'liq ism"
					rules={[
						{ required: true, message: "To'liq ismini kiriting" },
						{ max: 255, message: "Maksimum 255 ta belgi" },
					]}
				>
					<Input placeholder="Masalan: Aliyev Ali" id="staff-fullname" />
				</Form.Item>

				{showOrganization && (
					<Form.Item
						name="organizationId"
						label="Tashkilot"
						rules={[{ required: true, message: "Tashkilotni tanlang" }]}
					>
						<Select
							placeholder="Tanlang"
							options={organizations.map((o) => ({ value: o.id, label: o.name }))}
							showSearch
							filterOption={(input, option) =>
								(option?.label as string)?.toLowerCase().includes(input.toLowerCase())
							}
						/>
					</Form.Item>
				)}

				<Form.Item name="phoneNumber" label="Telefon raqami">
					<PhoneInput placeholder="90 123 45 67" id="staff-phone" />
				</Form.Item>

				<Form.Item name="jobEntryDate" label="Ishga kirgan sana">
					<Input type="date" id="staff-job-entry-date" />
				</Form.Item>

				<Form.Item name="positionId" label="Lavozim">
					<Select
						placeholder="Tanlang"
						loading={positionsLoading}
						showSearch
						allowClear
						filterOption={(input, option) =>
							(option?.label as string)?.toLowerCase().includes(input.toLowerCase())
						}
						options={(positionsData?.data?.items ?? []).map((p) => ({
							value: p.id,
							label: `${p.name} — ${p.type?.name ?? ""}`,
						}))}
					/>
				</Form.Item>

				{showBrigadeSelect && (
					<Form.Item name="brigadeId" label="Brigada">
						<Select
							placeholder="Tanlang"
							loading={brigadesLoading}
							showSearch
							allowClear
							filterOption={(input, option) =>
								(option?.label as string)?.toLowerCase().includes(input.toLowerCase())
							}
							options={(brigadesData?.data?.items ?? []).map((b) => ({
								value: b.id,
								label: b.name,
							}))}
						/>
					</Form.Item>
				)}

				{!brigadeId && (
					<Form.Item
						name="staffType"
						label="Xodim turi"
						rules={[{ required: true, message: "Xodim turini tanlang" }]}
					>
						<Select
							placeholder="Tanlang"
							options={
								(editingItem as typeof editingItem & { staffType?: StaffType })?.staffType ===
								"EMPLOYEE"
									? STAFF_TYPE_OPTIONS
									: ORG_STAFF_TYPE_OPTIONS
							}
							onChange={(value: StaffType) => {
								// Only EMPLOYEE staff can belong to a brigade — clear stale selection
								if (value !== "EMPLOYEE") {
									form.setFieldValue("brigadeId", undefined);
								}
							}}
						/>
					</Form.Item>
				)}

				{!brigadeId && (!editingItem || editingItem.userId || watchedStaffType === "BRIGADIER") && (
					<Form.Item
						name="password"
						label="Parol"
						rules={[{ min: 6, message: "Kamida 6 ta belgi" }]}
						extra={
							editingItem && !editingItem.userId && watchedStaffType === "BRIGADIER"
								? "Bu brigadirning hali hisobi yo'q — parol kiritilsa, telefon raqami bilan yangi hisob yaratiladi"
								: undefined
						}
					>
						<Input.Password placeholder="Parolni kiriting" id="staff-password" />
					</Form.Item>
				)}
				{(editingItem || brigadeId) && (
					<>
						<Form.Item
							name="maxHoursPerWeek"
							label="Haftalik ish soatlari"
							rules={[
								{ required: true, message: "Haftalik ish soatlarini kiriting" },
								{ type: "number", min: 1, max: 168, message: "1–168 oralig'ida bo'lishi kerak" },
							]}
						>
							<InputNumber min={1} max={168} style={{ width: "100%" }} placeholder="24" />
						</Form.Item>
						<Form.Item
							name="maxHoursPerDay"
							label="Kunlik ish soatlari"
							rules={[
								{ required: true, message: "Kunlik ish soatlarini kiriting" },
								{ type: "number", min: 1, max: 24, message: "1–24 oralig'ida bo'lishi kerak" },
							]}
						>
							<InputNumber min={1} max={24} style={{ width: "100%" }} placeholder="7" />
						</Form.Item>
					</>
				)}
				{editingItem && (
					<>
						<Form.Item name="workdays" label="Ish kunlari" className="col-span-2">
							<Checkbox.Group options={DAY_OPTIONS} className="flex flex-wrap gap-2" />
						</Form.Item>
						<Form.Item
							name="workStart"
							label="Ish boshlash vaqti"
							getValueFromEvent={(time) => (time ? dayjs(time).format("HH:mm:ss") : undefined)}
							getValueProps={(value) => ({
								value: value ? dayjs(value, "HH:mm:ss") : undefined,
							})}
						>
							<TimePicker format="HH:mm:ss" style={{ width: "100%" }} placeholder="08:00:00" />
						</Form.Item>
						<Form.Item
							name="workEnd"
							label="Ish tugash vaqti"
							getValueFromEvent={(time) => (time ? dayjs(time).format("HH:mm:ss") : undefined)}
							getValueProps={(value) => ({
								value: value ? dayjs(value, "HH:mm:ss") : undefined,
							})}
						>
							<TimePicker format="HH:mm:ss" style={{ width: "100%" }} placeholder="18:00:00" />
						</Form.Item>
					</>
				)}
				<Form.Item
					name="photoId"
					label="Rasm"
					className="col-span-2"
					rules={
						brigadeId && !editingItem
							? [
									{
										required: true,
										message: "Brigada xodimi uchun rasm (yuz ro'yxatga olish) talab qilinadi",
									},
								]
							: undefined
					}
					extra={
						editingItem?.hasFaceEnrollment
							? "Bu xodimning surati yuz ro'yxati orqali boshqariladi — o'zgartirish uchun \"Yuz ro'yxatga olish\" bo'limidan foydalaning"
							: undefined
					}
				>
					<ImageUpload
						initialUrl={editingItem?.photoUrl ?? null}
						style={{ width: "100%", height: "120px" }}
						disabled={Boolean(editingItem?.hasFaceEnrollment)}
					/>
				</Form.Item>
				{/* Hidden staffType for brigade context - ensures value is in form state on submit */}
				{brigadeId && (
					<Form.Item name="staffType" hidden initialValue="EMPLOYEE">
						<Input />
					</Form.Item>
				)}
			</div>
		</Form>
	);
}
